import { Drill, BarChart2, HelpCircle, Users, CheckSquare } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ChipAction =
  | { type: 'drill'; question: string }
  | { type: 'compare'; question: string }
  | { type: 'why'; question: string }
  | { type: 'ask-respondents-instead'; question: string }
  | { type: 'validate'; question: string }

interface FollowupChipRowProps {
  currentQuestion: string
  topAnswer: string
  onChipAction: (action: ChipAction) => void
}

type ChipId = ChipAction['type']

interface ChipDef {
  id: ChipId
  label: string
  icon: React.ElementType
}

export const CHIP_DEFS: ChipDef[] = [
  { id: 'drill', label: 'Drill down', icon: Drill },
  { id: 'compare', label: 'Compare segments', icon: BarChart2 },
  { id: 'why', label: 'Why?', icon: HelpCircle },
  { id: 'ask-respondents-instead', label: 'Ask respondents instead', icon: Users },
  { id: 'validate', label: 'Validate', icon: CheckSquare },
]

export function buildChipQuestion(
  chipId: ChipId,
  currentQuestion: string,
  topAnswer: string,
): string {
  switch (chipId) {
    case 'drill':
      return `Among which segment does "${topAnswer}" have the strongest support?`
    case 'compare':
      return `How does the prediction for "${currentQuestion}" differ between segments?`
    case 'why':
      return `What are the main reasons respondents would say "${topAnswer}"?`
    case 'ask-respondents-instead':
      return currentQuestion
    case 'validate':
      return currentQuestion
  }
}

export function FollowupChipRow({ currentQuestion, topAnswer, onChipAction }: FollowupChipRowProps) {
  if (!currentQuestion || !topAnswer) return null

  return (
    <div className={cn('flex flex-wrap gap-2 pt-2')}>
      {CHIP_DEFS.map((chip) => {
        const Icon = chip.icon
        return (
          <button
            key={chip.id}
            data-testid={`followup-chip-${chip.id}`}
            onClick={() =>
              onChipAction({
                type: chip.id,
                question: buildChipQuestion(chip.id, currentQuestion, topAnswer),
              })
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border-gray text-xs text-slate-600 hover:border-echo-indigo/40 hover:text-echo-indigo bg-white transition-colors"
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
            {chip.label}
          </button>
        )
      })}
    </div>
  )
}
